'use client';

import { LottieLoadingSpinner } from '@frontend/components/animations';
import { usePrivy } from '@privy-io/react-auth';
import { ReactNode } from 'react';

interface PrivyLoadingProviderProps {
  children: ReactNode;
}

/**
 * Provider that shows a full screen loading animation while Privy is initializing.
 *
 * Based on Privy documentation for waiting for Privy to be ready:
 * https://docs.privy.io/basics/react/setup#waiting-for-privy-to-be-ready
 *
 * This ensures that the app doesn't render until Privy has completed initialization,
 * including checking wallet connections, refreshing auth tokens, and fetching user data.
 */
export function PrivyLoadingProvider({ children }: PrivyLoadingProviderProps) {
  const { ready } = usePrivy();

  if (!ready) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center space-y-4">
          <LottieLoadingSpinner size={128} className="opacity-80" />
        </div>
      </div>
    );
  }

  // Privy is ready, render the app
  return <>{children}</>;
}

export default PrivyLoadingProvider;
